import React, { useEffect, useState } from 'react';
import Layout from '../../../components/Layout';
import Modal from '../../../components/Modal';
import ApiInspector from '../../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { formatCurrency, formatNumberInput } from '../../../utils/formatters';
import { API_DOCS } from './MovimentiFinanziamenti.api';
import {
  Cadenza, Modalita, FinanziamentoLite, RataDisponibile, FormState,
  CADENZA_LABEL, isoDate, fmtData, derivaPeriodoCompetenza, emptyForm,
  validaFormMovimento, buildPayloadMovimento, FormMovimento,
} from './movimentoForm';
import './MovimentiFinanziamenti.css';

// ── Tipi ──────────────────────────────────────────────────────────────────
interface MovimentoAggregato {
  id: number;
  dataPagamento: string;
  quotaInteressi: number;
  quotaCapitale: number;
  spese: number;
  cadenzaCompetenza: Cadenza;
  modalitaCompetenza: Modalita;
  periodoCompetenzaDa: string;
  periodoCompetenzaA: string;
  giorniIntersezione: number;
  giorniTotali: number;
  interessiPeriodo: number;
  capitalePeriodo: number;
  spesePeriodo: number;
}

interface FinanziamentoAggregato {
  finanziamentoId: number;
  descrizione: string;
  numeroFinanziamento?: string;
  banca?: { id: number; nome: string } | null;
  stato: string;
  interessiCompetenza: number;
  capitaleCompetenza: number;
  speseCompetenza: number;
  movimenti: MovimentoAggregato[];
}

interface AggregatoResponse {
  periodo: { da: string; a: string };
  finanziamenti: FinanziamentoAggregato[];
  totali: { interessi: number; capitale: number; spese: number };
}

interface Movimento {
  id: number;
  finanziamentoId: number;
  finanziamento?: { id: number; descrizione: string; numeroFinanziamento?: string; banca?: { nome: string } | null; cadenzaRata: Cadenza };
  societaId: number;
  societaNome?: string;
  dataPagamento: string;
  quotaCapitale: number;
  quotaInteressi: number;
  spese: number;
  cadenzaCompetenza: Cadenza;
  modalitaCompetenza: Modalita;
  periodoCompetenzaDa: string;
  periodoCompetenzaA: string;
  rataTeoricaId: number | null;
  rataTeorica?: { numeroRata: number; dataScadenza: string } | null;
  note?: string;
}

const MODULO = 'amministrazione.movimenti_finanziamenti';

// ── Componente principale ─────────────────────────────────────────────────
export default function MovimentiFinanziamenti() {
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const canDelete = can(MODULO, 'delete');

  const {
    societaId: filtroSocietaId,
    setSocietaId: setFiltroSocietaId,
    listaSocieta: societa,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  // Filtro periodo: default 1° gennaio anno corrente → oggi
  const oggi = new Date();
  const annoCorrente = oggi.getFullYear();
  const [periodoDa, setPeriodoDa] = useState<string>(`${annoCorrente}-01-01`);
  const [periodoA, setPeriodoA] = useState<string>(isoDate(new Date(Date.UTC(oggi.getFullYear(), oggi.getMonth(), oggi.getDate()))));

  const [tab, setTab] = useState<'aggregato' | 'movimenti'>('aggregato');

  // Dati
  const [aggregato, setAggregato] = useState<AggregatoResponse | null>(null);
  const [movimenti, setMovimenti] = useState<Movimento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  // Modale
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [finanziamentiLista, setFinanziamentiLista] = useState<FinanziamentoLite[]>([]);
  const [rateDisponibili, setRateDisponibili] = useState<RataDisponibile[]>([]);
  const [manuPeriodo, setManuPeriodo] = useState(false); // utente ha toccato il periodo

  // ── Caricamento ────────────────────────────────────────────────────────
  const load = async () => {
    if (!pronta) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filtroSocietaId) params.set('societaId', filtroSocietaId);
      if (periodoDa) params.set('periodoDa', periodoDa);
      if (periodoA) params.set('periodoA', periodoA);

      // @api GET /api/movimenti-finanziamento/aggregato?societaId=&periodoDa=&periodoA= -> { success: true, data: { periodo, finanziamenti, totali } }
      const [aggRes, listRes] = await Promise.all([
        apiFetch<{ success: boolean; data: AggregatoResponse }>(
          `/api/movimenti-finanziamento/aggregato?${params}`,
        ),
        // @api GET /api/movimenti-finanziamento?societaId=&periodoDa=&periodoA= -> { success: true, data: [{ id, finanziamentoId, finanziamento, dataPagamento, quotaCapitale, quotaInteressi, spese, periodoCompetenzaDa, periodoCompetenzaA }] }
        apiFetch<{ success: boolean; data: Movimento[] }>(
          `/api/movimenti-finanziamento?${params}`,
        ),
      ]);
      setAggregato(aggRes.data);
      setMovimenti(listRes.data ?? []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [pronta, filtroSocietaId, periodoDa, periodoA]);

  // ── Carica lista finanziamenti per la dropdown del modale ─────────────
  const loadFinanziamentiPerModale = async (societaId: number) => {
    if (!societaId) { setFinanziamentiLista([]); return; }
    try {
      // @api GET /api/finanziamenti?societaId= -> { finanziamenti: [{ id, descrizione, nomeBanca, cadenzaRata, numeroFinanziamento }] }
      const res = await apiFetch<{ finanziamenti: FinanziamentoLite[] }>(
        `/api/finanziamenti?societaId=${societaId}`,
      );
      setFinanziamentiLista(res.finanziamenti ?? []);
    } catch (e: any) {
      setFormError(e.message || 'Errore nel caricamento finanziamenti');
    }
  };

  // ── Carica rate teoriche disponibili per il finanziamento scelto ──────
  const loadRateDisponibili = async (finanziamentoId: number, includiId?: number) => {
    if (!finanziamentoId) { setRateDisponibili([]); return; }
    try {
      const params = new URLSearchParams({ finanziamentoId: String(finanziamentoId) });
      if (includiId) params.set('includiId', String(includiId));
      // @api GET /api/movimenti-finanziamento/rate-disponibili?finanziamentoId=&includiId= -> { success: true, data: [{ id, numeroRata, dataScadenza, importoRata, quotaCapitale, quotaInteressi }] }
      const res = await apiFetch<{ success: boolean; data: RataDisponibile[] }>(
        `/api/movimenti-finanziamento/rate-disponibili?${params}`,
      );
      setRateDisponibili(res.data ?? []);
    } catch (e: any) {
      setFormError(e.message || 'Errore nel caricamento rate teoriche');
    }
  };

  // ── Apri modale "nuovo" ───────────────────────────────────────────────
  const openCreate = async () => {
    const sid = filtroSocietaId ? Number(filtroSocietaId) : (societa[0]?.id ?? 0);
    setEditId(null);
    setFormError('');
    setForm(emptyForm(sid));
    setRateDisponibili([]);
    setManuPeriodo(false);
    setModalOpen(true);
    await loadFinanziamentiPerModale(sid);
  };

  // ── Apri modale "edit" ─────────────────────────────────────────────────
  const openEdit = async (m: Movimento) => {
    setEditId(m.id);
    setFormError('');
    setForm({
      societaId: m.societaId,
      finanziamentoId: String(m.finanziamentoId),
      dataPagamento: m.dataPagamento.slice(0, 10),
      quotaCapitale: formatNumberInput(m.quotaCapitale),
      quotaInteressi: formatNumberInput(m.quotaInteressi),
      spese: formatNumberInput(m.spese),
      cadenzaCompetenza: m.cadenzaCompetenza,
      modalitaCompetenza: m.modalitaCompetenza,
      periodoCompetenzaDa: m.periodoCompetenzaDa.slice(0, 10),
      periodoCompetenzaA: m.periodoCompetenzaA.slice(0, 10),
      rataTeoricaId: m.rataTeoricaId ? String(m.rataTeoricaId) : '',
      note: m.note || '',
    });
    setManuPeriodo(true);
    setModalOpen(true);
    await loadFinanziamentiPerModale(m.societaId);
    await loadRateDisponibili(m.finanziamentoId, m.rataTeoricaId ?? undefined);
  };

  // ── Quando cambia il finanziamento nel modale: imposta cadenza default + carica rate
  const onChangeFinanziamentoModale = async (finIdStr: string) => {
    const finId = Number(finIdStr);
    const fin = finanziamentiLista.find(f => f.id === finId);
    setForm(f => {
      const next = { ...f, finanziamentoId: finIdStr, rataTeoricaId: '' };
      if (fin) {
        next.cadenzaCompetenza = fin.cadenzaRata;
      }
      if (!manuPeriodo && next.dataPagamento) {
        const p = derivaPeriodoCompetenza(next.dataPagamento, next.cadenzaCompetenza, next.modalitaCompetenza);
        next.periodoCompetenzaDa = p.da;
        next.periodoCompetenzaA = p.a;
      }
      return next;
    });
    if (finId) await loadRateDisponibili(finId);
  };

  // ── Quando cambia dataPagamento/cadenza/modalità: ridiriva periodo
  const ricalcolaPeriodo = (next: FormState) => {
    if (!manuPeriodo && next.dataPagamento) {
      const p = derivaPeriodoCompetenza(next.dataPagamento, next.cadenzaCompetenza, next.modalitaCompetenza);
      next.periodoCompetenzaDa = p.da;
      next.periodoCompetenzaA = p.a;
    }
    return next;
  };

  // ── Salva ─────────────────────────────────────────────────────────────
  const save = async () => {
    const verr = validaFormMovimento(form);
    if (verr) { setFormError(verr); return; }

    setSaving(true);
    setFormError('');
    try {
      const payload = buildPayloadMovimento(form);
      if (editId) {
        // @api PUT /api/movimenti-finanziamento/:id { dataPagamento, quotaCapitale, quotaInteressi, spese, cadenzaCompetenza, modalitaCompetenza, periodoCompetenzaDa, periodoCompetenzaA, rataTeoricaId, note } -> { success: true }
        await apiFetch(`/api/movimenti-finanziamento/${editId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        // @api POST /api/movimenti-finanziamento { finanziamentoId, ... } -> { success: true, data: { id } }
        await apiFetch('/api/movimenti-finanziamento', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      setModalOpen(false);
      await load();
    } catch (e: any) {
      setFormError(e.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // ── Elimina ───────────────────────────────────────────────────────────
  const elimina = async (m: Movimento) => {
    if (!window.confirm(`Eliminare il movimento del ${fmtData(m.dataPagamento)}?`)) return;
    try {
      // @api DELETE /api/movimenti-finanziamento/:id -> { success: true }
      await apiFetch(`/api/movimenti-finanziamento/${m.id}`, { method: 'DELETE' });
      await load();
    } catch (e: any) {
      setError(e.message || 'Errore nell\'eliminazione');
    }
  };

  const toggleExpand = (id: number) => setExpanded(s => ({ ...s, [id]: !s[id] }));

  // ── Shortcut periodi ─────────────────────────────────────────────────
  const shortcutPeriodo = (key: string) => {
    const a = new Date();
    const annoCorr = a.getFullYear();
    if (key === 'anno-corrente') {
      setPeriodoDa(`${annoCorr}-01-01`);
      setPeriodoA(`${annoCorr}-12-31`);
    } else if (key === 'anno-precedente') {
      setPeriodoDa(`${annoCorr - 1}-01-01`);
      setPeriodoA(`${annoCorr - 1}-12-31`);
    } else if (key === 'ytd') {
      setPeriodoDa(`${annoCorr}-01-01`);
      setPeriodoA(isoDate(new Date(Date.UTC(a.getFullYear(), a.getMonth(), a.getDate()))));
    } else if (key === 'q1') {
      setPeriodoDa(`${annoCorr}-01-01`);
      setPeriodoA(`${annoCorr}-03-31`);
    } else if (key === 'q2') {
      setPeriodoDa(`${annoCorr}-04-01`);
      setPeriodoA(`${annoCorr}-06-30`);
    } else if (key === 'q3') {
      setPeriodoDa(`${annoCorr}-07-01`);
      setPeriodoA(`${annoCorr}-09-30`);
    } else if (key === 'q4') {
      setPeriodoDa(`${annoCorr}-10-01`);
      setPeriodoA(`${annoCorr}-12-31`);
    }
  };

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2>Movimenti finanziamenti</h2>
          <p>Pagamenti rate reali e calcolo competenze per periodo (ratei e risconti)</p>
        </div>
        {canEdit && (
          <button className="bop-btn bop-btn-primary" onClick={openCreate}>
            + Nuovo movimento
          </button>
        )}
      </div>

      {/* Filtri */}
      <div className="bop-filter-bar mov-fin-filter-bar">
        {!isUnica && (
          <select
            className="bop-select"
            value={filtroSocietaId}
            onChange={e => setFiltroSocietaId(e.target.value)}
          >
            <option value="">Tutte le società</option>
            {societa.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        )}
        <div className="mov-fin-periodo">
          <label className="bop-label mov-fin-periodo__label">Periodo competenza</label>
          <input
            type="date"
            className="bop-input"
            value={periodoDa}
            onChange={e => setPeriodoDa(e.target.value)}
          />
          <span className="mov-fin-periodo__sep">→</span>
          <input
            type="date"
            className="bop-input"
            value={periodoA}
            onChange={e => setPeriodoA(e.target.value)}
          />
        </div>
        <div className="mov-fin-shortcuts">
          <button type="button" className="bop-btn bop-btn-ghost" onClick={() => shortcutPeriodo('ytd')}>YTD</button>
          <button type="button" className="bop-btn bop-btn-ghost" onClick={() => shortcutPeriodo('q1')}>Q1</button>
          <button type="button" className="bop-btn bop-btn-ghost" onClick={() => shortcutPeriodo('q2')}>Q2</button>
          <button type="button" className="bop-btn bop-btn-ghost" onClick={() => shortcutPeriodo('q3')}>Q3</button>
          <button type="button" className="bop-btn bop-btn-ghost" onClick={() => shortcutPeriodo('q4')}>Q4</button>
          <button type="button" className="bop-btn bop-btn-ghost" onClick={() => shortcutPeriodo('anno-corrente')}>Anno corrente</button>
          <button type="button" className="bop-btn bop-btn-ghost" onClick={() => shortcutPeriodo('anno-precedente')}>Anno precedente</button>
        </div>
      </div>

      {/* Totali del periodo */}
      {aggregato && (
        <div className="mov-fin-totali">
          <div className="mov-fin-totali__card">
            <div className="mov-fin-totali__label">Interessi di competenza</div>
            <div className="mov-fin-totali__valore">{formatCurrency(aggregato.totali.interessi)}</div>
          </div>
          <div className="mov-fin-totali__card">
            <div className="mov-fin-totali__label">Capitale rimborsato</div>
            <div className="mov-fin-totali__valore">{formatCurrency(aggregato.totali.capitale)}</div>
          </div>
          <div className="mov-fin-totali__card">
            <div className="mov-fin-totali__label">Spese di competenza</div>
            <div className="mov-fin-totali__valore">{formatCurrency(aggregato.totali.spese)}</div>
          </div>
          <div className="mov-fin-totali__card mov-fin-totali__card--accent">
            <div className="mov-fin-totali__label">Costo complessivo</div>
            <div className="mov-fin-totali__valore">{formatCurrency(aggregato.totali.interessi + aggregato.totali.spese)}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bop-tabs">
        <button
          className={`bop-tab ${tab === 'aggregato' ? 'active' : ''}`}
          onClick={() => setTab('aggregato')}
        >
          Competenze per finanziamento
        </button>
        <button
          className={`bop-tab ${tab === 'movimenti' ? 'active' : ''}`}
          onClick={() => setTab('movimenti')}
        >
          Elenco movimenti caricati
        </button>
      </div>

      {error && <div className="bop-inline-error">{error}</div>}

      {/* TAB 1 — Aggregato per finanziamento */}
      {tab === 'aggregato' && (
        <div className="bop-card">
          <table className="bop-table">
            <thead>
              <tr>
                <th></th>
                <th>Finanziamento</th>
                <th>Banca</th>
                <th className="bop-text-right">Interessi competenza</th>
                <th className="bop-text-right">Capitale rimborsato</th>
                <th className="bop-text-right">Spese competenza</th>
                <th className="bop-text-right">Movimenti</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7}><div className="bop-empty-state">Caricamento…</div></td></tr>
              ) : !aggregato || aggregato.finanziamenti.length === 0 ? (
                <tr><td colSpan={7}><div className="bop-empty-state">Nessun movimento nel periodo selezionato</div></td></tr>
              ) : (
                aggregato.finanziamenti.map(f => (
                  <React.Fragment key={f.finanziamentoId}>
                    <tr className="mov-fin-row-fin" onClick={() => toggleExpand(f.finanziamentoId)}>
                      <td className="mov-fin-expand-cell">
                        <span className={`mov-fin-expand ${expanded[f.finanziamentoId] ? 'mov-fin-expand--open' : ''}`}>▸</span>
                      </td>
                      <td>
                        <div className="mov-fin-fin-nome">{f.descrizione}</div>
                        {f.numeroFinanziamento && <div className="bop-text-muted mov-fin-fin-numero">N. {f.numeroFinanziamento}</div>}
                      </td>
                      <td>{f.banca?.nome || '—'}</td>
                      <td className="bop-text-right">{formatCurrency(f.interessiCompetenza)}</td>
                      <td className="bop-text-right">{formatCurrency(f.capitaleCompetenza)}</td>
                      <td className="bop-text-right">{formatCurrency(f.speseCompetenza)}</td>
                      <td className="bop-text-right">{f.movimenti.length}</td>
                    </tr>
                    {expanded[f.finanziamentoId] && (
                      <tr className="mov-fin-row-dettagli">
                        <td colSpan={7}>
                          <table className="bop-table mov-fin-sub-table">
                            <thead>
                              <tr>
                                <th>Data pagamento</th>
                                <th>Cadenza</th>
                                <th>Modalità</th>
                                <th>Competenza</th>
                                <th className="bop-text-right">Interessi pagati</th>
                                <th className="bop-text-right">Capitale pagato</th>
                                <th className="bop-text-right">Spese</th>
                                <th className="bop-text-right">Giorni nel periodo</th>
                                <th className="bop-text-right">Interessi competenza</th>
                              </tr>
                            </thead>
                            <tbody>
                              {f.movimenti.map(m => (
                                <tr key={m.id}>
                                  <td>{fmtData(m.dataPagamento)}</td>
                                  <td>{CADENZA_LABEL[m.cadenzaCompetenza]}</td>
                                  <td>{m.modalitaCompetenza === 'ANTICIPATA' ? 'Anticipata' : 'Posticipata'}</td>
                                  <td>{fmtData(m.periodoCompetenzaDa)} → {fmtData(m.periodoCompetenzaA)}</td>
                                  <td className="bop-text-right">{formatCurrency(m.quotaInteressi)}</td>
                                  <td className="bop-text-right">{formatCurrency(m.quotaCapitale)}</td>
                                  <td className="bop-text-right">{formatCurrency(m.spese)}</td>
                                  <td className="bop-text-right">{m.giorniIntersezione} / {m.giorniTotali}</td>
                                  <td className="bop-text-right mov-fin-cell-evidenza">{formatCurrency(m.interessiPeriodo)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB 2 — Elenco movimenti */}
      {tab === 'movimenti' && (
        <div className="bop-card">
          <table className="bop-table">
            <thead>
              <tr>
                <th>Data pagamento</th>
                <th>Finanziamento</th>
                <th>Banca</th>
                <th className="bop-text-right">Quota interessi</th>
                <th className="bop-text-right">Quota capitale</th>
                <th className="bop-text-right">Spese</th>
                <th>Periodo competenza</th>
                <th>Rata teorica</th>
                <th className="bop-text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9}><div className="bop-empty-state">Caricamento…</div></td></tr>
              ) : movimenti.length === 0 ? (
                <tr><td colSpan={9}><div className="bop-empty-state">Nessun movimento nel periodo selezionato</div></td></tr>
              ) : (
                movimenti.map(m => (
                  <tr key={m.id}>
                    <td>{fmtData(m.dataPagamento)}</td>
                    <td>{m.finanziamento?.descrizione || `#${m.finanziamentoId}`}</td>
                    <td>{m.finanziamento?.banca?.nome || '—'}</td>
                    <td className="bop-text-right">{formatCurrency(m.quotaInteressi)}</td>
                    <td className="bop-text-right">{formatCurrency(m.quotaCapitale)}</td>
                    <td className="bop-text-right">{formatCurrency(m.spese)}</td>
                    <td>{fmtData(m.periodoCompetenzaDa)} → {fmtData(m.periodoCompetenzaA)}</td>
                    <td>
                      {m.rataTeorica
                        ? `Rata n. ${m.rataTeorica.numeroRata}`
                        : <span className="bop-badge bop-badge-neutral">Extra-piano</span>}
                    </td>
                    <td className="bop-text-right">
                      <BopRowActions actions={[
                        { icon: <RowIcon.Edit />, label: 'Modifica', onClick: () => openEdit(m), hidden: !canEdit },
                        { icon: <RowIcon.Trash />, label: 'Elimina', onClick: () => elimina(m), variant: 'danger', hidden: !canDelete },
                      ]} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modale crea/modifica */}
      {modalOpen && (
        <Modal
          title={editId ? 'Modifica movimento finanziamento' : 'Nuovo movimento finanziamento'}
          onClose={() => setModalOpen(false)}
          size="lg"
          footer={
            <>
              <button className="bop-btn bop-btn-secondary" onClick={() => setModalOpen(false)} disabled={saving}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Salvataggio…' : (editId ? 'Salva modifiche' : 'Crea movimento')}
              </button>
            </>
          }
        >
          <FormMovimento
            form={form}
            setForm={setForm}
            finanziamentiLista={finanziamentiLista}
            rateDisponibili={rateDisponibili}
            disabledFinanziamento={!!editId}
            onChangeFinanziamento={onChangeFinanziamentoModale}
            onTouchPeriodo={() => setManuPeriodo(true)}
            ricalcolaPeriodo={ricalcolaPeriodo}
            error={formError}
          />
        </Modal>
      )}

      <ApiInspector docs={API_DOCS as any} />
    </Layout>
  );
}
